import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { WorksPermitAnalysis } from "@costadorada/shared";

/**
 * Shown to the owner when a submitted solicitud appears to conflict with a rule.
 *
 * Deliberately cautious. The sentence it quotes has been verified to exist in the
 * document, but whether it applies to this particular request is a judgement the board
 * makes, not the machine. So this reads as "possible conflict, the board decides",
 * never as a refusal — a request rejected by a machine the board never consulted would
 * be both wrong and unfair.
 *
 * It also deliberately shows less than the board sees: the full AiAnalysisPanel carries
 * a recommendation aimed at the Junta, which should not reach the applicant before the
 * Junta has ruled.
 */
export function RegulationConflictPanel({
  analysis,
  clearedNote,
}: {
  analysis: WorksPermitAnalysis;
  clearedNote?: string | null;
}) {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("en") !== true;
  const tr = (spanish: string, english: string) => (es ? spanish : english);

  // The board dismissed this conflict — say so instead of leaving a warning standing.
  if (clearedNote !== undefined && clearedNote !== null) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-900">
          <ShieldCheck size={16} />
          {tr("La Junta revisó este aviso y lo descartó", "The board reviewed this notice and dismissed it")}
        </div>
        {clearedNote && <p className="mt-1 text-sm text-emerald-800">{clearedNote}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle size={16} />
        {tr("Posible conflicto con el reglamento", "Possible conflict with the rules")}
      </div>

      {analysis.prohibitedReasonEs && <p className="text-sm text-amber-900">{analysis.prohibitedReasonEs}</p>}

      {analysis.prohibitedQuoteEs && (
        <blockquote className="border-l-2 border-amber-400 pl-3 text-sm italic text-amber-900">
          “{analysis.prohibitedQuoteEs}”
          {analysis.prohibitedSourceEs && (
            <footer className="mt-1 not-italic text-xs text-amber-700">— {analysis.prohibitedSourceEs}</footer>
          )}
        </blockquote>
      )}

      <p className="text-xs text-amber-800">
        {tr(
          "Su solicitud fue recibida y quedó registrada. La Junta de Directores la evaluará y toma la decisión final; " +
            "este aviso es una verificación automática contra los documentos del condominio, no una decisión.",
          "Your request was received and saved. The board will review it and makes the final decision; this notice is " +
            "an automatic check against the condominium's documents, not a decision."
        )}
      </p>
    </div>
  );
}

/** Shown while the automatic check is still running (it takes around a minute). */
export function RegulationCheckPending() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("en") !== true;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
      {es ? "Verificando la solicitud contra el reglamento…" : "Checking the request against the rules…"}
    </div>
  );
}
